import { inject, type InjectionKey, type Ref, ref, watch } from 'vue'

import type { PendingAction } from '@/stores/aiAssistant/types'

import { formKeysAffectedByAiAction } from '../lib/onboardingFormMapping.ts'
import type { OnboardingFormData } from '../lib/onboardingPrefill'

/** Contact-person field → ambassador field copied while "Same as contact person" is checked. */
const CONTACT_TO_AMBASSADOR = {
  contactFirstName: 'ambassadorFirstName',
  contactLastName: 'ambassadorLastName',
  contactEmail: 'ambassadorEmail',
  contactPhone: 'phone',
} as const
type ContactKey = keyof typeof CONTACT_TO_AMBASSADOR
const CONTACT_KEYS = Object.keys(CONTACT_TO_AMBASSADOR) as ContactKey[]
const COPIED_AMBASSADOR_FIELDS = new Set<string>(Object.values(CONTACT_TO_AMBASSADOR))

export type AmbassadorSameAsContact = {
  sameAsContact: Ref<boolean>
  toggle: (checked: boolean) => void
}

export const ambassadorSameAsContactKey: InjectionKey<AmbassadorSameAsContact> =
  Symbol('ambassadorSameAsContact')

/**
 * "Same as contact person" for the ambassador section. Lives in the wizard
 * (index.vue), not the data-entry form, so contact edits made on the review
 * step still update the copied ambassador fields.
 *
 * `persistField` must write the value without going through `noteFieldEdit`,
 * otherwise the copy itself would uncheck the box.
 */
export function useAmbassadorSameAsContact(options: {
  formData: OnboardingFormData
  persistField: (field: string, value: string) => void
}) {
  const { formData, persistField } = options
  const sameAsContact = ref(false)

  function copyContactToAmbassador() {
    for (const contactKey of CONTACT_KEYS) {
      const value = formData[contactKey] || ''
      const ambassadorKey = CONTACT_TO_AMBASSADOR[contactKey]
      if (formData[ambassadorKey] !== value) persistField(ambassadorKey, value)
    }
  }

  function toggle(checked: boolean) {
    sameAsContact.value = checked
    if (!checked) return
    copyContactToAmbassador()
    // Mobile is only seeded once from the contact phone, never kept in sync.
    if (!formData.ambassadorMobile && formData.contactPhone) {
      persistField('ambassadorMobile', formData.contactPhone)
    }
  }

  // Keep copied fields in sync while checked.
  watch(
    () => CONTACT_KEYS.map((key) => formData[key]),
    () => {
      if (sameAsContact.value) copyContactToAmbassador()
    }
  )

  /**
   * Call before applying a user edit. Nothing is ever locked: editing a copied
   * ambassador field just unchecks the box and keeps the edited value.
   */
  function noteFieldEdit(field: string, value: unknown) {
    if (!COPIED_AMBASSADOR_FIELDS.has(field)) return
    // PhoneField re-emits its value on blur, so only a real change counts as an edit.
    const current = (formData as Record<string, unknown>)[field] ?? ''
    if ((value ?? '') !== current) sameAsContact.value = false
  }

  /**
   * Call before a confirmed AI action is applied to the form. An assistant
   * update of a copied ambassador field is an independent edit, so it unchecks
   * the box like a manual edit would (otherwise the next contact edit would
   * overwrite the confirmed value). Contact-person actions keep it checked.
   */
  function noteConfirmedAiAction(action: PendingAction) {
    if (action.mutation !== 'updateAmbassador') return
    const pv = action.proposedValue
    if (pv === undefined || pv === null) return
    const value = typeof pv === 'string' ? pv : String(pv)
    const keys = formKeysAffectedByAiAction(action, formData as unknown as Record<string, unknown>)
    for (const key of keys) noteFieldEdit(key, value)
  }

  return { sameAsContact, toggle, noteFieldEdit, noteConfirmedAiAction }
}

export function useAmbassadorSameAsContactContext(): AmbassadorSameAsContact {
  const ctx = inject(ambassadorSameAsContactKey)
  if (!ctx) {
    throw new Error('useAmbassadorSameAsContactContext() must be used inside the onboarding wizard')
  }
  return ctx
}
